import React, { useState, useEffect } from "react";
import { Button, message, Radio, InputNumber, Modal, Input } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { QuestionCircleOutlined } from "@ant-design/icons";
import axiosInstance from "@/axios/axiosInstance";
import "./PayConfirm.scss";
import PaySuccess from "./PaySuccess";

const PayConfirm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState({});
  const [payMethod, setPayMethod] = useState("balance");
  const [userInfo, setUserInfo] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!location.state?.orderInfo) {
      message.error("订单信息不完整");
      navigate(-1);
      return;
    }
    setOrderInfo(location.state.orderInfo);
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await axiosInstance.get("/account/detail");
      if (response.data.code === 10000) {
        setUserInfo(response.data.data);
      }
    } catch (error) {
      console.error("获取用户信息失败:", error);
    }
  };

  const handleQuantityChange = (value) => {
    setQuantity(value);
  };

  const handleSendCode = async () => {
    try {
      const phone = userInfo.phone;
      if (!phone) {
        message.error("获取手机号失败");
        return;
      }

      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      const response = await axiosInstance.get(
        `/account/sendVerifyCode?phone=${phone}`
      );
      if (response.data.code === 10000) {
        message.success("验证码已发送");
      } else {
        message.error(response.data.msg || "发送验证码失败");
      }
    } catch (error) {
      console.error("发送验证码失败:", error);
      message.error("发送验证码失败");
    }
  };

  const handlePayClick = () => {
    if (!hasEnoughBalance) {
      handleRecharge();
      return;
    }
    setVerifyModalVisible(true);
    handleSendCode();
  };

  const handleVerifySubmit = async () => {
    if (!verifyCode) {
      message.warning("请输入验证码");
      return;
    }

    try {
      setLoading(true);
      const verifyResponse = await axiosInstance.post("/account/verifyCode", {
        phone: userInfo.phone,
        code: verifyCode,
      });

      if (verifyResponse.data.code !== 10000) {
        message.error("验证码错误");
        return;
      }

      const response = await axiosInstance.post("/compute/order/pay", {
        computeId: orderInfo.id,
        quantity: quantity,
        payMethod: payMethod,
      });

      if (response.data.code === 10000) {
        setVerifyModalVisible(false);
        setShowSuccess(true);
        // 3秒后自动跳转到订单中心
        setTimeout(() => {
          navigate("/personal", { state: { activeKey: "66" } });
        }, 5000);
      } else {
        message.error(response.data.msg || "支付失败");
      }
    } catch (error) {
      console.error("支付失败:", error);
      message.error("支付失败");
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = () => {
    // setShowSuccess(true);
    navigate(
      "/personal?selectedKey=10&openKeys=sub1&openKeys=sub23&openKeys=sub22"
    );
  };

  const totalAmount =
    (orderInfo.hirePrice * orderInfo.hireTime || 0) * quantity;
  const hasEnoughBalance = (userInfo.balance || 0) >= totalAmount;
  const neics = [
    { key: 1, value: "2GiB" },
    { key: 2, value: "4GiB " },
    { key: 3, value: "8GiB" },
    { key: 4, value: "16GiB" },
    { key: 5, value: "32GiB" },
    { key: 6, value: "64GiB" },
    { key: 7, value: "128GiB" },
    { key: 8, value: "256GiB" },
    { key: 9, value: "512GiB" },
  ];
  const cpus = [
    { key: 1, value: "1核(vCPU)" },
    { key: 2, value: "2核(vCPU) " },
    { key: 3, value: "4核(vCPU)" },
    { key: 4, value: "8核(vCPU)" },
    { key: 5, value: "16核(vCPU)" },
    { key: 6, value: "32核(vCPU)" },
    { key: 7, value: "48核(vCPU)" },
  ];

  if (showSuccess) {
    return (
      <PaySuccess orderInfo={{ ...orderInfo, balance: userInfo.balance }} />
    );
  }

  return (
    <div className="pay-confirm-container">
      <div className="pay-confirm-header">
        <div className="title">确认支付信息</div>
        <div
          className="order-center"
          onClick={() =>
            navigate("/personal?selectedKey=66&openKeys=sub1&openKeys=sub3")
          }
        >
          订单中心
        </div>
      </div>

      <div className="content">
        <div className="user-info-section">
          <div className="section-title">购买用户信息</div>
          <div className="user-info">
            <div className="user-name">{userInfo.nickname}</div>
            <div className="user-detail">
              工作年限：{userInfo.workYears}年<span className="divider">|</span>
              手机号：{userInfo.phone}
              <span className="divider">|</span>
              邮箱：{userInfo.email}
            </div>
          </div>
        </div>

        <div className="payment-section">
          <div className="section-title">支付方式</div>
          <Radio.Group
            onChange={(e) => setPayMethod(e.target.value)}
            value={payMethod}
          >
            <div className="payment-option">
              <Radio value="balance">
                余额支付
                <span className="balance-amount">
                  账户余额：{userInfo.balance || 0}
                  <QuestionCircleOutlined className="help-icon" />
                </span>
              </Radio>
              <div className="balance-tip">
                当前仅支持账户余额支付，如余额不足先进行充值
              </div>
            </div>
          </Radio.Group>
        </div>

        <div className="product-section">
          <div className="section-title">商品信息</div>
          <div className="product-info">
            <div className="seller">卖家：{orderInfo.sellerName}</div>
            <div className="product-name">
              {orderInfo.area} {orderInfo.gpu} {orderInfo.systemEnvironment}
            </div>
            <div className="product-specs">
              显存：{orderInfo.graphicsMemory} GB CPU:{" "}
              {orderInfo.cpu
                ? cpus.find((item) => item.key === orderInfo.cpu).value
                : ""}
              {"   "} 内存：{" "}
              {orderInfo.memory
                ? neics.find((item) => item.key === orderInfo.memory).value
                : ""}
              存储空间：
              {orderInfo.graphicsMemory}GB 购买时长： {orderInfo.hireTime}
              {orderInfo.hireType === 1
                ? "天"
                : orderInfo.hireType === 2
                ? "月"
                : "年"}
            </div>
            <div className="product-price">
              <div className="price-label">单价：</div>
              <div className="price-value">¥{orderInfo.hirePrice}</div>
              <div className="quantity-control">
                <div className="quantity-label">数量：</div>
                <InputNumber
                  min={1}
                  max={1}
                  value={quantity}
                  onChange={handleQuantityChange}
                />
              </div>
              <div className="subtotal">
                小计：
                <span className="amount">
                  ¥ {orderInfo.hirePrice * orderInfo.hireTime}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="summary-section">
          <div className="summary-row">
            <span>总商品金额：</span>
            <span className="amount">
              ¥{orderInfo.hirePrice * orderInfo.hireTime}
            </span>
          </div>
          <div className="summary-row">
            <span>手续费：</span>
            <span className="amount">¥0.00</span>
          </div>
          <div className="summary-row">
            <span>账户余额：</span>
            <span className="amount">{userInfo.balance || 0}</span>
          </div>
          <div className="summary-row total">
            <span>应付总额：</span>
            <span className="amount highlight">
              ¥{orderInfo.hirePrice * orderInfo.hireTime}
            </span>
          </div>
          <div className="user-info-display">
            购买用户：{userInfo.nickname} {userInfo.phone}
          </div>
        </div>

        <div className="action-section">
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handlePayClick}
            className={hasEnoughBalance ? "pay-button" : "recharge-button"}
          >
            {hasEnoughBalance ? "确认支付" : "账户充值"}
          </Button>
        </div>
      </div>

      <Modal
        title="手机验证"
        open={verifyModalVisible}
        onCancel={() => setVerifyModalVisible(false)}
        footer={null}
        maskClosable={false}
        className="verify-modal"
      >
        <div className="verify-phone">
          <div className="phone-label">验证手机：</div>
          <div className="phone-number">{userInfo.phone}</div>
        </div>

        <div className="verify-code-section">
          <Input
            placeholder="请输入验证码"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value)}
            maxLength={6}
          />
          <Button
            type="primary"
            onClick={handleSendCode}
            disabled={countdown > 0}
            className="code-button"
          >
            {countdown > 0 ? `${countdown}s后重新获取` : "点击获取"}
          </Button>
        </div>

        <div className="modal-footer">
          <Button onClick={() => setVerifyModalVisible(false)}>取消</Button>
          <Button type="primary" loading={loading} onClick={handleVerifySubmit}>
            确认支付
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PayConfirm;
